import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { ShareWebService } from './share.web.service';
import { IdentityService } from 'identity';
import { Guid } from 'DiscoveryEngine.TS/Utils';
import { ShareDto, ShareTypes, GetSharesResultDto, NetViewDto, AgentDto, UserDto } from "UvExplorerServer.Dtos.TS";
import { DiscoverySettings, ScheduledDiscovery } from '../../DiscoveryEngine.TS/Discovery';
import { NetViewTreeNodeDto } from '../../UvExplorerServer.Dtos.TS/NetViews';

@Injectable()
export class ShareService implements OnDestroy {

	public areSharesLoaded: boolean = false;

	public sourceShares: ShareDto[] = [];
	public targetShares: ShareDto[] = [];

	public targetNetViews: NetViewDto[] = [];
	public targetRootNetView: NetViewDto = null;
	public targetNetworks: NetViewDto[] = [];
	public targetAgents: AgentDto[] = [];

	public sharesLoaded: Subject<any>;
	public onSharesLoaded: Observable<any>;

	public sharesChanged: Subject<any>;
	public onSharesChanged: Observable<any>;

	public sharesUnloaded: Subject<any>;
	public onSharesUnloaded: Observable<any>;

	private loginSub: Subscription;
	private logoutSub: Subscription;

	constructor(private identityService: IdentityService, private shareWebService: ShareWebService) {
		// Shares were loaded from the server
		this.sharesLoaded = new Subject<any>();
		this.onSharesLoaded = this.sharesLoaded.asObservable();

		// Updates/changes/modifications were made to the shares
		this.sharesChanged = new Subject<any>();
		this.onSharesChanged = this.sharesChanged.asObservable();

		// Shares were unloaded at logout
		this.sharesUnloaded = new Subject<any>();
		this.onSharesUnloaded = this.sharesUnloaded.asObservable();

		if (identityService.isLoggedIn) {
			this.populateShares();
		}

		this.loginSub = identityService.onLogin.subscribe((user: UserDto) => {
			this.populateShares();
		});

		this.logoutSub = identityService.onLogout.subscribe(() => {
			this.sourceShares = [];
			this.targetShares = [];

			this.targetNetViews = [];
			this.targetRootNetView = null;
			this.targetNetworks = [];
			this.targetAgents = [];

			this.sharesUnloaded.next(true);
		});
	}

	public ngOnDestroy(): void {
		if (this.loginSub) {
			this.loginSub.unsubscribe();
			this.loginSub = undefined;
		}
	}

	public reloadShares(): Observable<any> {
		return this.populateShares();
	}

	public getShares(): Observable<GetSharesResultDto> {
		return this.shareWebService.getShares(ShareTypes.Network, "all");
	}

	private populateShares(): Observable<any> {
		let subject = new Subject<any>();

		this.getShares().subscribe(
			(result: GetSharesResultDto) => {

				this.sourceShares = result.SourceShares.ToArray();
				this.targetShares = result.TargetShares.ToArray();

				this.targetAgents = result.TargetNetViewTree.Agents.ToArray();
				this.targetNetViews = result.TargetNetViewTree.NetViews.ToArray(); // Gets all netviews INCLUDING THE ROOT OF THE TREE
				this.targetNetworks = [];
				this.targetRootNetView = null;

				this.targetNetViews.forEach((netview: NetViewDto) => {
					if (netview.ScopeGuid == "") {
						this.targetRootNetView = netview;
					}
					else {
						this.targetNetworks.push(netview);
					}
				});

				this.areSharesLoaded = true;
				
				subject.next(true);
				this.sharesLoaded.next(true);
			},
			(error?: any) => {
				console.log(JSON.stringify(error));
				subject.error(error);
			});

		return subject.asObservable();
	}

	public getTargetNetworkByGuid(guid: string): NetViewDto {
		for (let i = 0; i < this.targetNetworks.length; ++i) {
			let network: NetViewDto = this.targetNetworks[i];
			if (network.Guid == guid) {
				return network;
			}
		}
		return null;
	}

	public getTargetAgentByGuid(guid: string): AgentDto {
		for (let i = 0; i < this.targetAgents.length; ++i) {
			let agent: AgentDto = this.targetAgents[i];
			if (agent.Guid == guid) {
				return agent;
			}
		}
		return null;
	}

	public getTargetAgentParent(agentGuid: string): NetViewDto {
		let agent: AgentDto = this.getTargetAgentByGuid(agentGuid);
		if (agent) {
			return this.getTargetNetworkByGuid(agent.ScopeGuid);
		}
		return null;
	}

	public getTargetSchedDiscParent(schedDiscGuid: string): AgentDto {
		for (let i = 0; i < this.targetAgents.length; ++i) {
			let agent = this.targetAgents[i];
			for (let j = 0; j < agent.ScheduledDiscoveries.Count; ++j) {
				let schedDisc = agent.ScheduledDiscoveries.Get(j);
				if (schedDisc.GUID === schedDiscGuid) {
					return agent;
				}
			}
		}
		return null;
	}

	public getSourceSharesForItem(sharedItemGuid: string): ShareDto[] {
		return this.getSharesForItem(this.sourceShares, sharedItemGuid);
	}

	public getTargetSharesForItem(sharedItemGuid: string): ShareDto[] {
		return this.getSharesForItem(this.targetShares, sharedItemGuid);
	}

	private getSharesForItem(sharesToSearch: ShareDto[], sharedItemGuid: string): ShareDto[] {
		let result: ShareDto[] = [];
		sharesToSearch.forEach((share: ShareDto) => {
			if (share.SharedItemGuid == sharedItemGuid) {
				result.push(share);
			}
		});
		return result;
	}

	public createShare(newShare: ShareDto): Observable<ShareDto> {
		let subject = new Subject<ShareDto>();

		this.shareWebService.createShare(newShare).subscribe(
			(result: ShareDto) => {
				this.addSourceShare(result);
				subject.next(result);
				this.sharesChanged.next(true);
			},
			(error?) => {
				console.log(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public updateShare(share: ShareDto): Observable<ShareDto> {
		let subject = new Subject<ShareDto>();

		this.shareWebService.updateShare(share).subscribe(
			(result: ShareDto) => {
				subject.next(result);
				this.sharesChanged.next(true);
			},
			(error?) => {
				console.log(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteSourceShare(guid: string): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.shareWebService.deleteShareByGuid(guid).subscribe(
			(result: boolean) => {
				this.removeSourceShare(guid);
				subject.next(result);
				this.sharesChanged.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteTargetShare(guid: string): Observable<boolean> {
		let subject = new Subject<boolean>();

		this.shareWebService.deleteShareByGuid(guid).subscribe(
			(result: boolean) => {
				this.removeTargetShare(guid);
				subject.next(result);
				this.sharesChanged.next(true);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	private addSourceShare(share: ShareDto): void {
		let index = -1;

		console.assert(share.HasTargetUserGuid !== share.HasTargetGroupGuid);

		if (share.HasTargetUserGuid) {
			for (let i = 0; i < this.sourceShares.length; ++i) {
				let s: ShareDto = this.sourceShares[i];
				if (s.SourceUserGuid == share.SourceUserGuid && s.TargetUserGuid == share.TargetUserGuid && s.SharedItemGuid == share.SharedItemGuid) {
					index = i;
					break;
				}
			}
		}
		else if (share.HasTargetGroupGuid) {
			for (let i = 0; i < this.sourceShares.length; ++i) {
				let s: ShareDto = this.sourceShares[i];
				if (s.SourceUserGuid == share.SourceUserGuid && s.TargetGroupGuid == share.TargetGroupGuid && s.SharedItemGuid == share.SharedItemGuid) {
					index = i;
					break;
				}
			}
		}

		if (index >= 0) {
			this.sourceShares[index] = share;
		}
		else {
			this.sourceShares.push(share);
		}
	}

	private removeSourceShare(guid: string): void {
		let foundShare: boolean = false;

		for (let i = 0; i < this.sourceShares.length; ++i) {
			let s: ShareDto = this.sourceShares[i];
			if (s.Guid == guid) {
				this.sourceShares.splice(i, 1);
				foundShare = true;
				break;
			}
		}

		console.assert(foundShare);
	}

	private removeTargetShare(guid: string): void {
		let foundShare: boolean = false;
		let foundRootChild: boolean = false;
		let foundNetView: boolean = false;
		let foundNetwork: boolean = false;

		for (let i = 0; i < this.targetShares.length; ++i) {
			let share: ShareDto = this.targetShares[i];
			if (share.Guid == guid) {

				this.targetShares.splice(i, 1);

				foundRootChild = this.targetRootNetView.NetViewGuids.Remove(share.SharedItemGuid);

				for (let j = 0; j < this.targetNetViews.length; ++j) {
					let netView: NetViewDto = this.targetNetViews[j];
					if (netView.Guid == share.SharedItemGuid) {
						this.targetNetViews.splice(j, 1);
						foundNetView = true;
						break;
					}
				}

				for (let j = 0; j < this.targetNetworks.length; ++j) {
					let netView: NetViewDto = this.targetNetworks[j];
					if (netView.Guid == share.SharedItemGuid) {
						this.targetNetworks.splice(j, 1);
						foundNetwork = true;
						break;
					}
				}

				let k = 0;
				while (k < this.targetAgents.length) {
					let agent: AgentDto = this.targetAgents[k];
					if (agent.ScopeGuid == share.SharedItemGuid) {
						this.targetAgents.splice(k, 1);
					}
					else {
						++k;
					}
				}

				foundShare = true;
				break;
			}
		}

		console.assert(foundShare);
		console.assert(foundRootChild);
		console.assert(foundNetView);
		console.assert(foundNetwork);
	}

	public getTargetSchedDiscSettings(schedDiscGuid: string, discSettingsGuid: string): DiscoverySettings {
		let settings: DiscoverySettings = null;

		let agent: AgentDto = this.getTargetSchedDiscParent(schedDiscGuid);
		if (agent) {
			agent.DiscoverySettings.Some((ds: DiscoverySettings) => {
				if (ds.GUID == discSettingsGuid) {
					settings = ds;
					return true;
				}
				else {
					return false;
				}
			}, this);
		}

		return settings;
	}

	public getTargetProtocolSettingsReferences(psGuid: string): string[] {
		psGuid = psGuid.toLowerCase();

		let nodes: NetViewTreeNodeDto[] = [...this.targetNetViews, ...this.targetAgents];

		let result: string[] = [];

		nodes.forEach((node: NetViewTreeNodeDto) => {
			node.DiscoverySettings.ForEach((ds: DiscoverySettings) => {
				if (ds.ProtocolSettings.Some((guid: Guid) => {
					return (guid.ToString().toLowerCase() == psGuid);
				}, this)) {
					result.push(ds.Name);
				}
			}, this);
			node.ScheduledDiscoveries.ForEach((sd: ScheduledDiscovery) => {
				if (sd.PrtgExport.HasServerSettings) {
					if (sd.PrtgExport.ServerSettings.ToString().toLowerCase() == psGuid) {
						result.push(sd.Name);
					}
				}
			}, this);
		}, this);

		return result;
	}

	/*PRIVATE*/ public addTargetAgent(newAgent: AgentDto, parent: NetViewDto): void {
		parent.AgentGuids.Add(newAgent.Guid);
		this.targetAgents.push(newAgent);
	}

	/*PRIVATE*/ public removeTargetAgent(guid: string): void {
		let parent: NetViewDto = this.getTargetAgentParent(guid);
		if (parent) {
			parent.AgentGuids.Remove(guid);

			for (let i = 0; i < this.targetAgents.length; ++i) {
				if (this.targetAgents[i].Guid == guid) {
					this.targetAgents.splice(i, 1);
					break;
				}
			}
		}
	}

}